import React, { useEffect, useState } from 'react';
import './About.css';
import '../Animation.css';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import reactImage from '../../assets/images/react.webp';
import nodeImage from '../../assets/images/node.webp';
import database from '../../assets/images/database.webp';
import www from '../../assets/images/www.webp';
import projects from '../../assets/images/projects.webp';
import chat from '../../assets/images/chat.webp';

import { getAboutSections, updateAboutSection } from '../../api/api';
import { getAsideEntries } from '../../api/api';
import { useAuthStore } from '../../store/authStore';

// wspólny loader
import Loader from '../../components/Loader/Loader';

const About = () => {
  const { isAuthenticated } = useAuthStore();

  // sekcje tekstowe (AboutMe)
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);
  const [errorSections, setErrorSections] = useState(null);

  // Dane kontaktowe
  const [infoData, setInfoData] = useState([]);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [errorInfo, setErrorInfo] = useState(null);

  // Edit tekstu
  const [editingKey, setEditingKey] = useState(null);
  const [editContent, setEditContent] = useState('');

  // Stat lista umiejętności
  const skills = [
    { icon: 'devicon-react-original colored', name: 'React.js' },
    { icon: 'devicon-nextjs-plain colored', name: 'Next.js' },
    { icon: 'devicon-javascript-plain colored', name: 'JavaScript' },
    { icon: 'devicon-html5-plain colored', name: 'HTML5' },
    { icon: 'devicon-css3-plain colored', name: 'CSS3' },
    { icon: 'devicon-tailwindcss-plain colored', name: 'Tailwind CSS' },
    { icon: 'devicon-nodejs-plain colored', name: 'Node.js' },
    { icon: 'devicon-express-original colored', name: 'Express.js' },
    { icon: 'devicon-postgresql-plain colored', name: 'PostgreSQL' },
    { icon: 'devicon-git-plain colored', name: 'Git' },
  ];

  // ladowanie globalne
  const loading = loadingSections || loadingInfo;

  // preload obrazków
  useEffect(() => {
    const imagePaths = [reactImage, nodeImage, database, projects, chat, www];

    imagePaths.forEach((path) => {
      const img = new Image();
      img.src = path;
    });
  }, []);

  // pobieranie sekcji tekstowych
  useEffect(() => {
    const fetchSections = async () => {
      setLoadingSections(true);
      try {
        const data = await getAboutSections();
        const sorted = data.sort((a, b) => a.sortOrder - b.sortOrder);
        setSections(sorted);
      } catch (err) {
        setErrorSections(err.message || 'Nie udało się załadować treści');
        console.error('Błąd pobierania sekcji AboutMe:', err);
      } finally {
        setLoadingSections(false);
      }
    };
    fetchSections();
  }, []);

  useEffect(() => {
    const fetchInfo = async () => {
      setLoadingInfo(true);
      try {
        const data = await getAsideEntries('info');
        const onlyInfo = data.filter((item) => item.type === 'info');
        setInfoData(onlyInfo.sort((a, b) => a.sortOrder - b.sortOrder));
        //console.log('Pobrane dane typu "info":', onlyInfo);
      } catch (err) {
        setErrorInfo(
          err.message || 'Nie udało się załadować danych kontaktowych'
        );
        console.error('Błąd pobierania danych info:', err);
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchInfo();
  }, []);

  const phoneEntry = infoData.find((item) =>
    item.title?.toLowerCase().includes('telefon')
  );
  const nr_tel = phoneEntry?.subtitle || '+48 888 508 630';

  const renderMultilineText = (text) => {
    if (!text) return null;
    return text
      .replace(/\\n/g, '\n')
      .split('\n')
      .map((line, index) => <p key={index}>{line}</p>);
  };

  const getSectionByKey = (key) => sections.find((s) => s.sectionKey === key);
  const getContentByKey = (key) => {
    const section = getSectionByKey(key);
    return section ? section.content.trim() : '';
  };

  const startEditing = (key) => {
    setEditingKey(key);
    setEditContent(getContentByKey(key));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditContent('');
  };

  const saveEdit = async (key) => {
    const trimmed = editContent.trim();
    if (!trimmed) {
      alert('Treść nie może być pusta');
      return;
    }

    const section = getSectionByKey(key);
    if (!section) {
      alert('Nie znaleziono sekcji w bazie');
      return;
    }

    if (trimmed === section.content.trim()) {
      cancelEdit();
      return;
    }

    try {
      await updateAboutSection(section.id, { content: trimmed });
      setSections((prev) =>
        prev.map((s) => (s.id === section.id ? { ...s, content: trimmed } : s))
      );
      alert('Zapisano zmiany!');
      cancelEdit();
    } catch (err) {
      console.error('Błąd zapisu sekcji:', err);
      alert('Błąd zapisu: ' + (err.message || 'Nieznany błąd'));
    }
  };

  // Ikonka fallback
  const getInfoIconClass = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('email')) return 'fas fa-envelope';
    if (t.includes('telefon')) return 'fas fa-phone';
    if (t.includes('lokalizacja') || t.includes('miejsce'))
      return 'fas fa-map-marker-alt';
    if (t.includes('uczelnia') || t.includes('szkoła'))
      return 'fas fa-graduation-cap';
    return 'fas fa-info-circle';
  };

  // Ładowanie jeden wspólny loader
  if (loading) {
    return <Loader />;
  }

  if (errorSections || errorInfo) {
    return (
      <div className="error">
        Błąd: {errorSections || errorInfo || 'Nieznany problem'}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <html lang="pl" />
        <title>
          O mnie - Mikołaj Melnyk | Web Developer React & Full-Stack
        </title>
        <meta
          name="description"
          content="Poznaj mnie – junior React developer | web developer. Specjalizuję się w React, Next.js, Node.js, Express, Tailwind CSS i PostgreSQL. Tworzę responsywne aplikacje webowe, strony i projekty full-stack ."
        />
        <meta
          name="keywords"
          content="o mnie, mikolaj melnyk, web developer, portfolio, react developer, fullstack, next.js, node.js, tailwind css, postgresql, javascript, frontend, backend, rzeszów, junior developer, programista"
        />
        <meta
          property="og:title"
          content="O mnie - Mikołaj Melnyk | React • Next.js • Node.js Developer"
        />
        <meta
          property="og:description"
          content="Junior web developer – React, Next.js i Node.js. Tworzę nowoczesne aplikacje i strony internetowe. Dowiedz się więcej o moim podejściu, technologiach i projektach."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mikolajmelnyk.pl/" />
        <meta property="og:locale" content="pl_PL" />
        <meta property="og:site_name" content="Portfolio Mikołaj Melnyk" />

        {/* Dodatkowe meta – dobre dla SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Mikołaj Melnyk" />
        <meta name="revisit-after" content="7 days" />
      </Helmet>

      <div className="about-container">
        <h1 className="title_h1">O mnie</h1>

        <div className="about-me-text-opis">
          <div className="about-me-text">
            <p>
              Witam! <span>Nazywam się Mikołaj</span>
            </p>
          </div>

          <div className="editable-block">
            {isAuthenticated && (
              <button
                className="edit-icon-btn"
                onClick={() => startEditing('intro')}
                title="Edytuj sekcję"
              >
                <i className="fas fa-pencil-alt"></i>
              </button>
            )}

            {editingKey === 'intro' ? (
              <div className="edit-mode">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '180px',
                    fontSize: '1.1rem',
                    lineHeight: '1.6',
                  }}
                />
                <div className="edit-buttons">
                  <button
                    onClick={() => saveEdit('intro')}
                    className="save-btn"
                  >
                    <i className="fas fa-check"></i> Zapisz
                  </button>
                  <button onClick={cancelEdit} className="cancel-btn">
                    <i className="fas fa-times"></i> Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <div className="about-text">
                {renderMultilineText(getContentByKey('intro'))}
              </div>
            )}
          </div>
        </div>

        <h2 className="sub_title_h1">Czym się zajmuję?</h2>

        <div className="section-what-im-doing">
          <div
            className={`p1 ${editingKey === 'web-development' ? 'editing' : ''}`}
          >
            <div className="column-photo1"></div>
            <div className="column-text">
              <h3>Web Development</h3>
              <div className="doing-text editable-block">
                {isAuthenticated && (
                  <button
                    className="edit-icon-btn small"
                    onClick={() => startEditing('web-development')}
                    title="Edytuj"
                  >
                    <i className="fas fa-pencil-alt"></i>
                  </button>
                )}

                {editingKey === 'web-development' ? (
                  <div className="edit-mode">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      style={{ width: '100%', minHeight: '80px' }}
                    />
                    <div className="edit-buttons">
                      <button
                        onClick={() => saveEdit('web-development')}
                        className="save-btn"
                      >
                        <i className="fas fa-check"></i> Zapisz
                      </button>
                      <button onClick={cancelEdit} className="cancel-btn">
                        <i className="fas fa-times"></i> Anuluj
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="doing-text">
                    {renderMultilineText(getContentByKey('web-development'))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`p2 ${editingKey === 'react' ? 'editing' : ''}`}>
            <div className="column-photo2"></div>
            <div className="column-text">
              <h3>React.js</h3>
              <div className="doing-text editable-block">
                {isAuthenticated && (
                  <button
                    className="edit-icon-btn small"
                    onClick={() => startEditing('react')}
                    title="Edytuj"
                  >
                    <i className="fas fa-pencil-alt"></i>
                  </button>
                )}

                {editingKey === 'react' ? (
                  <div className="edit-mode">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      style={{ width: '100%', minHeight: '80px' }}
                    />
                    <div className="edit-buttons">
                      <button
                        onClick={() => saveEdit('react')}
                        className="save-btn"
                      >
                        <i className="fas fa-check"></i> Zapisz
                      </button>
                      <button onClick={cancelEdit} className="cancel-btn">
                        <i className="fas fa-times"></i> Anuluj
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="doing-text">
                    {renderMultilineText(getContentByKey('react'))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`p3 ${editingKey === 'node' ? 'editing' : ''}`}>
            <div className="column-photo3"></div>
            <div className="column-text">
              <h3>Node.js</h3>
              <div className="doing-text editable-block">
                {isAuthenticated && (
                  <button
                    className="edit-icon-btn small"
                    onClick={() => startEditing('node')}
                    title="Edytuj"
                  >
                    <i className="fas fa-pencil-alt"></i>
                  </button>
                )}

                {editingKey === 'node' ? (
                  <div className="edit-mode">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      style={{ width: '100%', minHeight: '80px' }}
                    />
                    <div className="edit-buttons">
                      <button
                        onClick={() => saveEdit('node')}
                        className="save-btn"
                      >
                        <i className="fas fa-check"></i> Zapisz
                      </button>
                      <button onClick={cancelEdit} className="cancel-btn">
                        <i className="fas fa-times"></i> Anuluj
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="doing-text">
                    {renderMultilineText(getContentByKey('node'))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`p4 ${editingKey === 'databases' ? 'editing' : ''}`}>
            <div className="column-photo4"></div>
            <div className="column-text">
              <h3>Databases</h3>
              <div className="doing-text editable-block">
                {isAuthenticated && (
                  <button
                    className="edit-icon-btn small"
                    onClick={() => startEditing('databases')}
                    title="Edytuj"
                  >
                    <i className="fas fa-pencil-alt"></i>
                  </button>
                )}

                {editingKey === 'databases' ? (
                  <div className="edit-mode">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      style={{ width: '100%', minHeight: '80px' }}
                    />
                    <div className="edit-buttons">
                      <button
                        onClick={() => saveEdit('databases')}
                        className="save-btn"
                      >
                        <i className="fas fa-check"></i> Zapisz
                      </button>
                      <button onClick={cancelEdit} className="cancel-btn">
                        <i className="fas fa-times"></i> Anuluj
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="doing-text">
                    {renderMultilineText(getContentByKey('databases'))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="section-hobby">
          <h2 className="hobby">Zainteresowania</h2>
          <div className="line"></div>
          <div className="editable-block">
            {isAuthenticated && (
              <button
                className="edit-icon-btn"
                onClick={() => startEditing('hobbies')}
                title="Edytuj sekcję"
              >
                <i className="fas fa-pencil-alt"></i>
              </button>
            )}

            {editingKey === 'hobbies' ? (
              <div className="edit-mode">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  style={{ width: '100%', minHeight: '220px' }}
                />
                <div className="edit-buttons">
                  <button
                    onClick={() => saveEdit('hobbies')}
                    className="save-btn"
                  >
                    <i className="fas fa-check"></i> Zapisz
                  </button>
                  <button onClick={cancelEdit} className="cancel-btn">
                    <i className="fas fa-times"></i> Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <div className="hobby-text">
                {renderMultilineText(getContentByKey('hobbies'))}
              </div>
            )}
          </div>
          <div className="hobby-actions">
            <Link to="/resume" className="hobby-btn ">
              <span>Sprawdź pełne Resume</span>
              <i className="fas fa-file-alt"></i>
            </Link>
          </div>
        </div>

        <div className="Skills">
          <h2 className="sub_title_h1_up">Umiejętności zawodowe</h2>
          <div className="skills-carousel">
            <div className="skills-track">
              {skills.map((skill, index) => (
                <div key={index} className="skill">
                  <i className={skill.icon}></i>
                  <p>{skill.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="Why-it">
          <h2 className="sub_title_h1">Dlaczego wybrałem programowanie?</h2>
          <div className="line"></div>

          <div className="story-text editable-block">
            {isAuthenticated && (
              <button
                className="edit-icon-btn"
                onClick={() => startEditing('why-programming')}
                title="Edytuj sekcję"
              >
                <i className="fas fa-pencil-alt"></i>
              </button>
            )}

            {editingKey === 'why-programming' ? (
              <div className="edit-mode">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '320px',
                    fontSize: '1.05rem',
                  }}
                />
                <div className="edit-buttons">
                  <button
                    onClick={() => saveEdit('why-programming')}
                    className="save-btn"
                  >
                    <i className="fas fa-check"></i> Zapisz
                  </button>
                  <button onClick={cancelEdit} className="cancel-btn">
                    <i className="fas fa-times"></i> Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <div className="story-text-content">
                {renderMultilineText(getContentByKey('why-programming'))}
              </div>
            )}
          </div>

          {/* Przycisk do bloga – najlepiej na dole sekcji */}
          <div className="why-actions">
            <Link to="/Blog" className="hobby-btn hobby-btn--blog">
              Czytaj moje artykuły i przemyślenia
              <i className="fas fa-blog"></i>
            </Link>
          </div>
        </div>

        <div className="Contact">
          <div className="contact-text">
            <h2 className="contact-title">
              Masz pytania lub potrzebujesz wsparcia przy projekcie?
            </h2>
            <span className="contact-line"></span>
            <p className="contact-subtitle">
              Skontaktuj się ze mną, aby omówić szczegóły i razem stworzyć coś
              wyjątkowego.
            </p>
          </div>

          <Link to="/Contact" className="buttoninfo">
            <h2 className="contact-click">
              Kliknij tutaj, aby się skontaktować{' '}
              <i className="fas fa-phone"></i>
            </h2>
          </Link>
        </div>

        {/* TYLKO dane typu "info" */}
        <div className="container-information-about">
          {infoData.length === 0 ? (
            <p className="no-info">Brak danych kontaktowych w bazie</p>
          ) : (
            infoData.map((item) => (
              <div key={item.id} className="info-about">
                <div className="column-pht">
                  {item.iconUrl ? (
                    <img src={item.iconUrl} alt={item.title} />
                  ) : (
                    <i className={getInfoIconClass(item.title)}></i>
                  )}
                </div>
                <div className="column-txt">
                  <h3 className="title">{item.title}:</h3>
                  <p className="text">
                    {item.title?.toLowerCase().includes('telefon') ? (
                      <CopyToClipboard text={item.subtitle || nr_tel}>
                        <span className="clickable-phone">
                          {item.subtitle || nr_tel}
                        </span>
                      </CopyToClipboard>
                    ) : item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.subtitle}
                      </a>
                    ) : (
                      item.subtitle
                    )}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default About;
